import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { AlertTriangle, CalendarClock, GripVertical, Heart, LayoutTemplate, Loader2, Lock, MessageCircle, Plus, UserX } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { usePermissions } from '@hooks/usePermissions';
import { useSocialPost } from '@contexts/SocialPostContext';
import { useSelectedSocialAccount } from '@hooks/useSelectedSocialAccount';
import socialService from '@services/socialService';
import type { Network, SocialGrid, SocialPost } from '@/types/social';
import { NETWORKS, NETWORK_LABELS } from '@/types/social';
import { colorClasses } from '@/utils/colors';
import { GRID_ASPECT, formatPostTime, isLocked, moveInOrder, timeOf } from '@/utils/social';
import { formatDayMonthBR } from '@/utils/formatters';
import { ApprovedSeal, NetworkIcon, PillarDot, PostCover, StatusChip } from '@components/social/SocialBits';
import { ProfileSwitcher } from '@components/social/ProfileSwitcher';
import { ProfileHeader } from '@components/social/ProfileHeader';

/** Equilíbrio dos pilares: participação na grade versus a meta. */
export const PillarBalancePanel: React.FC<{ balance: SocialGrid['balance']; title?: string }> = ({ balance, title = 'Equilíbrio dos pilares' }) => (
  <section className="card p-3 space-y-2">
    <h3 className="text-xs font-semibold uppercase text-slate-500">{title}</h3>
    {balance.length === 0 && <p className="text-xs text-slate-400">Sem posts no período.</p>}
    {balance.map((b) => {
      const cc = colorClasses(b.color);
      const off = b.gap !== undefined && Math.abs(b.gap) >= 10;
      return (
        <div key={`${b.pillar_id ?? 'none'}`} className="space-y-0.5">
          <div className="flex items-center justify-between text-xs">
            <PillarDot color={b.color} name={b.name} />
            <span className={clsx(off ? 'text-amber-700 font-semibold' : 'text-slate-500')}>
              {b.percent.toLocaleString('pt-BR')}% {b.target_percent !== undefined && `· meta ${b.target_percent}%`}
            </span>
          </div>
          <div className="relative h-2 rounded bg-slate-100 overflow-hidden">
            <div className={clsx('h-full', cc.swatch)} style={{ width: `${Math.min(100, b.percent)}%` }} />
            {b.target_percent !== undefined && (
              <span className="absolute top-0 h-full w-0.5 bg-slate-700" style={{ left: `${Math.min(100, b.target_percent)}%` }} title={`Meta ${b.target_percent}%`} />
            )}
          </div>
        </div>
      );
    })}
  </section>
);

/**
 * Grade visual (estilo Plann): a prévia de UM perfil — cabeçalho com foto, bio e números, os posts
 * planejados do mais recente para o mais antigo, como o perfil vai ficar, seguidos do feed já
 * publicado. Arrastar um post sobre outro troca a ordem e os horários são redistribuídos
 * (POST /social/grid/reorder). Documentação: src/docs/redes-sociais/grade.md.
 */
export const SocialGridView: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const network = (params.get('network') as Network) || 'instagram';
  const { accounts, accountId, setAccountId, ready } = useSelectedSocialAccount({ network, required: true });
  const [grid, setGrid] = useState<SocialGrid | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  const [overId, setOverId] = useState<number | null>(null);
  const { openPost, openNewPost, version } = useSocialPost();
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('social.write').hasPermission;
  const canManage = checkPermission('social.accounts.manage').hasPermission;

  // ?post=ID abre o post (links do sino e da atividade).
  useEffect(() => {
    const id = Number(params.get('post'));
    if (id > 0) {
      openPost(id);
      params.delete('post');
      setParams(params, { replace: true });
    }
  }, [params, setParams, openPost]);

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    try {
      setGrid(await socialService.grid(network, accountId ?? undefined));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [network, accountId, ready]);

  useEffect(() => {
    load();
  }, [load, version]);

  const netAccounts = accounts.filter((a) => a.network === network && a.is_active);
  const profile = grid?.account;
  const planned = useMemo(() => grid?.planned ?? [], [grid]);
  const newPost = () => openNewPost(profile ? { account: { id: profile.id, network: profile.network } } : {});

  const syncProfile = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      await socialService.syncFeed(profile.id);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const drop = async (target: SocialPost) => {
    const from = dragId;
    setDragId(null);
    setOverId(null);
    if (!from || from === target.id || !grid) return;
    const movable = planned.filter((p) => !isLocked(p.status));
    const order = moveInOrder(
      movable.map((p) => p.id),
      from,
      target.id,
    );
    setSaving(true);
    try {
      await socialService.reorder(order);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const tile = (p: SocialPost) => {
    const locked = isLocked(p.status);
    const errors = p.issues.filter((i) => i.level === 'error').length;
    // Variação da rede sem conta escolhida: aparece em todos os perfis até alguém escolher.
    const unassigned = p.targets.some((t) => t.network === network && !t.account_id) && !p.targets.some((t) => t.account_id === profile?.id);
    return (
      <div
        key={p.id}
        draggable={canWrite && !locked}
        onDragStart={() => setDragId(p.id)}
        onDragEnd={() => {
          setDragId(null);
          setOverId(null);
        }}
        onDragOver={(e) => {
          if (dragId && !locked) {
            e.preventDefault();
            setOverId(p.id);
          }
        }}
        onDragLeave={() => setOverId((o) => (o === p.id ? null : o))}
        onDrop={(e) => {
          e.preventDefault();
          drop(p);
        }}
        onClick={() => openPost(p.id)}
        className={clsx(
          'group relative cursor-pointer overflow-hidden bg-white',
          GRID_ASPECT[network],
          overId === p.id && 'ring-4 ring-emerald-400 z-10',
          dragId === p.id && 'opacity-40',
        )}
        title={`${p.title} · ${p.scheduled_at ? formatPostTime(p.scheduled_at) : 'sem data'}`}
      >
        <PostCover post={p} className="absolute inset-0 w-full h-full" />
        <div className="absolute top-1 left-1 flex items-center gap-1">
          <ApprovedSeal status={p.status} />
          {errors > 0 && p.status !== 'strategy' && (
            <span className="rounded-full bg-white/95 p-0.5 shadow" title={`${errors} problema(s) a corrigir`}>
              <AlertTriangle size={14} className="text-red-600" />
            </span>
          )}
          {unassigned && netAccounts.length > 1 && (
            <span className="rounded-full bg-white/95 p-0.5 shadow" title="Sem perfil escolhido: aparece na grade de todos os perfis">
              <UserX size={14} className="text-amber-600" />
            </span>
          )}
        </div>
        <div className="absolute top-1 right-1 flex items-center gap-1">
          {locked ? (
            <Lock size={12} className="text-white drop-shadow" />
          ) : (
            canWrite && <GripVertical size={14} className="text-white drop-shadow opacity-0 group-hover:opacity-100" />
          )}
        </div>
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 pb-1 pt-4 text-[10px] text-white flex items-center justify-between gap-1">
          <span className="truncate">
            {p.scheduled_at ? `${formatDayMonthBR(p.scheduled_at.slice(0, 10))} ${timeOf(p.scheduled_at)}` : 'Sem data'}
          </span>
          {p.pillar_color && <span className={clsx('w-2 h-2 rounded-full flex-shrink-0', colorClasses(p.pillar_color).swatch)} />}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1">
          {NETWORKS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => {
                params.set('network', n);
                params.delete('account');
                setParams(params, { replace: true });
              }}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm border',
                n === network ? 'bg-white border-slate-300 font-semibold shadow-sm' : 'border-transparent text-slate-500 hover:bg-white',
              )}
            >
              <NetworkIcon network={n} /> {NETWORK_LABELS[n]}
            </button>
          ))}
          {(loading || saving) && <Loader2 size={16} className="animate-spin text-slate-400 ml-2" />}
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" icon={<LayoutTemplate />} onClick={() => openNewPost({ strategy: true, title: 'Bloco de estratégia' })}>
              Bloco de estratégia
            </Button>
            <Button size="sm" icon={<Plus />} onClick={newPost}>
              Novo post
            </Button>
          </div>
        )}
      </div>

      <ProfileSwitcher accounts={netAccounts} value={profile?.id ?? accountId} onChange={setAccountId} />

      <div className="flex flex-col xl:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <div className="mx-auto max-w-[620px]">
            {profile ? (
              <ProfileHeader
                account={profile}
                planned={planned.length}
                feed={grid?.feed.length ?? 0}
                onSync={canManage ? syncProfile : undefined}
                syncing={saving}
              />
            ) : (
              ready &&
              !loading && (
                <p className="mb-3 text-xs text-slate-500">
                  Nenhuma conta de {NETWORK_LABELS[network]} cadastrada — a grade mostra os posts da rede sem perfil.
                </p>
              )
            )}
            {planned.length === 0 && (grid?.feed.length ?? 0) === 0 && !loading && (
              <div className="card p-8 text-center text-slate-500 space-y-2">
                <CalendarClock size={32} className="mx-auto text-slate-300" />
                <p>Nada planejado para {NETWORK_LABELS[network]} ainda.</p>
                <p className="text-xs">Crie um post ou arraste blocos de estratégia para montar o esqueleto do feed.</p>
              </div>
            )}
            <div className="grid grid-cols-3 gap-0.5 bg-slate-200">
              {planned.map(tile)}
              {(grid?.feed ?? []).map((f) => (
                <a
                  key={`feed-${f.id}`}
                  href={f.permalink}
                  target="_blank"
                  rel="noreferrer"
                  className={clsx('relative overflow-hidden bg-slate-100', GRID_ASPECT[network])}
                  title={f.caption}
                >
                  {f.thumbnail_url && <img src={f.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                  <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[10px] text-white inline-flex items-center gap-1">
                    <Heart size={9} /> {f.like_count ?? 0} <MessageCircle size={9} /> {f.comment_count ?? 0}
                  </span>
                </a>
              ))}
            </div>
            {network === 'instagram' && (grid?.feed.length ?? 0) === 0 && planned.length > 0 && (
              <p className="mt-2 text-[11px] text-slate-400 text-center">
                Conecte a conta em Redes sociais → Contas conectadas para ver aqui o feed já publicado.
              </p>
            )}
          </div>
        </div>

        <aside className="xl:w-80 flex-shrink-0 space-y-4">
          <PillarBalancePanel balance={grid?.balance ?? []} title="Equilíbrio dos pilares na grade" />
          <section className="card p-3 space-y-2">
            <h3 className="text-xs font-semibold uppercase text-slate-500">Sem data ({grid?.unscheduled.length ?? 0})</h3>
            <p className="text-[11px] text-slate-500">Já aparecem na grade e podem ser arrastados. Abra o post para escolher a data.</p>
            <ul className="space-y-1.5 max-h-[420px] overflow-y-auto custom-scrollbar">
              {(grid?.unscheduled ?? []).map((p) => (
                <li key={p.id}>
                  <button type="button" onClick={() => openPost(p.id)} className="w-full flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-1.5 text-left hover:border-slate-300">
                    <PostCover post={p} className="w-10 h-10 rounded flex-shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-medium text-slate-700">{p.title}</span>
                      <StatusChip status={p.status} className="mt-0.5" />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
          <section className="card p-3 text-[11px] text-slate-500 space-y-1">
            <p>
              <strong>Arraste</strong> um post sobre outro para trocar a ordem — os horários já marcados são redistribuídos (o mais
              recente fica no topo). Post sem data guarda o lugar escolhido. Publicados não se movem.
            </p>
            <p>O selo verde indica aprovado pelo gestor; o triângulo vermelho, algo a corrigir antes de enviar.</p>
          </section>
        </aside>
      </div>
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};

export default SocialGridView;
